import React from "react";
import {
  Box, IconButton, InputAdornment, Popover, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";

// Icons offered for phases. Chosen for phase-vocabulary coverage
// (baseline / assessment / acquisition / mastered / maintenance / hold /
// generalization / probe / closed / discontinued). Each entry gives the
// MUI icon name string (stored to phase.icon) and its rendered component.
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import LayersIcon from "@mui/icons-material/Layers";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import RepeatIcon from "@mui/icons-material/Repeat";
import PanToolIcon from "@mui/icons-material/PanTool";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import BlockIcon from "@mui/icons-material/Block";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import FlagIcon from "@mui/icons-material/Flag";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { LegacyTokens, B13, B13Ink, tint12 } from "../../theme/b13Tokens";

export const PHASE_ICONS: Record<string, React.ComponentType<any>> = {
  AssessmentOutlined:   AssessmentOutlinedIcon,
  Layers:               LayersIcon,
  EmojiEvents:          EmojiEventsIcon,
  Repeat:               RepeatIcon,
  PanTool:              PanToolIcon,
  AssignmentOutlined:   AssignmentOutlinedIcon,
  DoneAll:              DoneAllIcon,
  Block:                BlockIcon,
  HourglassEmpty:       HourglassEmptyIcon,
  DirectionsRun:        DirectionsRunIcon,
  AutoFixHigh:          AutoFixHighIcon,
  School:               SchoolIcon,
  Star:                 StarIcon,
  Flag:                 FlagIcon,
  PlayArrow:            PlayArrowIcon,
  CheckCircleOutline:   CheckCircleOutlineIcon,
};

export function resolvePhaseIcon(name: string | null | undefined): React.ComponentType<any> {
  if (!name) return FlagIcon; // sensible default when phase.icon is null
  return PHASE_ICONS[name] ?? FlagIcon;
}

export type IconPickerProps = {
  value: string | null;
  onChange: (iconName: string) => void;
  disabled?: boolean;
};

export default function IconPicker({ value, onChange, disabled = false }: IconPickerProps) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [query, setQuery] = React.useState("");
  const open = Boolean(anchor);

  const CurrentIcon = resolvePhaseIcon(value);

  const names = Object.keys(PHASE_ICONS);
  const filtered = query.trim()
    ? names.filter((n) => n.toLowerCase().includes(query.trim().toLowerCase()))
    : names;

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Phase icon"
        onClick={(e) => { if (!disabled) setAnchor(e.currentTarget); }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setAnchor(e.currentTarget as HTMLElement);
          }
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          border: `1px solid ${LegacyTokens.line}`,
          borderRadius: `${LegacyTokens.radius.control}px`,
          px: 1.5,
          py: 1,
          cursor: disabled ? "not-allowed" : "pointer",
          "&:hover": disabled ? {} : { borderColor: B13.primary },
          "&:focus-visible": { outline: `2px solid ${B13.primary}`, outlineOffset: 2 },
          minWidth: 90,
          justifyContent: "space-between",
        }}
      >
        <CurrentIcon sx={{ color: LegacyTokens.ink, fontSize: 22 }} />
        <ArrowDropDownIcon sx={{ color: B13Ink.ink3 }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); setQuery(""); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 2, width: 320,
            borderRadius: `${LegacyTokens.radius.control}px`,
            boxShadow: "0 6px 20px rgba(15, 61, 46, 0.14)",
          },
        }}
      >
        <TextField
          fullWidth size="small" autoFocus placeholder="Search icons"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: B13Ink.ink3 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0.5 }}>
          {filtered.map((name) => {
            const Ic = PHASE_ICONS[name];
            const selected = name === value;
            return (
              <Tooltip key={name} title={name} arrow>
                <IconButton
                  onClick={() => { onChange(name); setAnchor(null); setQuery(""); }}
                  aria-label={name}
                  sx={{
                    borderRadius: `${LegacyTokens.radius.control}px`,
                    color: selected ? B13.primary : LegacyTokens.ink,
                    backgroundColor: selected ? tint12(B13.primary) : "transparent",
                    "&:hover": { backgroundColor: B13.canvas },
                  }}
                >
                  <Ic sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>
            );
          })}
        </Box>
        {filtered.length === 0 && (
          <Typography sx={{ fontSize: 12, color: B13Ink.ink3, textAlign: "center", py: 2 }}>
            No icons match "{query}".
          </Typography>
        )}
      </Popover>
    </>
  );
}
